import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Table, Button } from 'react-bootstrap';
import { X } from 'lucide-react';
import { formatMoney, currencySymbol } from './TotalsPanel';
import { calcLine } from '../../pages/quotes/quoteUtils';

function OptionPills({ options }) {
  if (!options || typeof options !== 'object') return null;
  const entries = Object.entries(options).filter(([, v]) => v != null && String(v).trim() !== '');
  if (!entries.length) return null;
  return (
    <div className="d-flex flex-wrap gap-1 mt-1">
      {entries.map(([key, value]) => (
        <span
          key={key}
          className="badge rounded-pill text-bg-light border fw-normal text-secondary"
          style={{ fontSize: 11 }}
        >
          {key}: {String(value)}
        </span>
      ))}
    </div>
  );
}

function formatMargin(line, currency) {
  if (line.margin == null || line.marginAmount == null) return '—';
  return `${formatMoney(line.marginAmount, currency)} (${Number(line.margin).toFixed(2)}%)`;
}

const QuoteGrid = ({ items = [], currency = 'USD', editable = false, onChange, onRemove }) => {
  const { t } = useTranslation();
  const updateItem = (index, patch) => {
    if (!onChange) return;
    const next = items.map((item, i) => (i === index ? calcLine({ ...item, ...patch }) : item));
    onChange(next);
  };

  if (!items.length) {
    return (
      <div className="table-responsive bg-white rounded border">
        <Table className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>{t('quoteGrid.image')}</th>
              <th>{t('quoteGrid.product')}</th>
              <th>{t('quoteGrid.qty')}</th>
              <th>{t('quoteGrid.costPrice')}</th>
              <th>{t('quoteGrid.price')}</th>
              <th>{t('quoteGrid.discountedPrice')}</th>
              <th>{t('quoteGrid.lineTotal')}</th>
              <th>{t('quoteGrid.margin')}</th>
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={editable ? 9 : 8} className="text-center text-muted py-4">
                {t('quoteGrid.empty')}
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  }

  return (
    <div className="table-responsive bg-white rounded border">
      <Table hover className="mb-0 align-middle">
        <thead className="table-light">
          <tr>
            <th style={{ width: 56 }}>{t('quoteGrid.image')}</th>
            <th>{t('quoteGrid.product')}</th>
            <th style={{ width: 80 }}>{t('quoteGrid.qty')}</th>
            <th style={{ width: 110 }}>{t('quoteGrid.costPrice')}</th>
            <th style={{ width: 100 }}>{t('quoteGrid.price')}</th>
            <th style={{ width: 140 }}>{t('quoteGrid.discountedPrice')}</th>
            <th style={{ width: 110 }}>{t('quoteGrid.lineTotal')}</th>
            <th style={{ width: 130 }}>{t('quoteGrid.margin')}</th>
            {editable && <th style={{ width: 48 }} />}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const line = calcLine(item);
            return (
              <tr key={item.id || `${item.productId}-${index}`}>
                <td>
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt=""
                      width={40}
                      height={40}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        if (e.currentTarget.nextSibling) {
                          e.currentTarget.nextSibling.style.display = 'block';
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className="bg-light border rounded"
                    style={{
                      width: 40,
                      height: 40,
                      display: item.imageUrl ? 'none' : 'block',
                    }}
                  />
                </td>
                <td>
                  <div className="fw-semibold">{item.name}</div>
                  {item.sku && <div className="small text-muted">{t('quoteGrid.skuLabel', { sku: item.sku })}</div>}
                  <OptionPills options={item.options} />
                  {item.isCustom && <div className="small text-warning">{t('quoteGrid.custom')}</div>}
                </td>
                <td>
                  {editable ? (
                    <Form.Control
                      size="sm"
                      type="number"
                      min="1"
                      value={line.qty}
                      onChange={(e) => updateItem(index, { qty: parseInt(e.target.value, 10) || 1 })}
                    />
                  ) : (
                    line.qty
                  )}
                </td>
                <td>
                  {editable ? (
                    <Form.Control
                      size="sm"
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.costPrice != null ? line.costPrice : ''}
                      onChange={(e) =>
                        updateItem(index, {
                          costPrice: e.target.value === '' ? undefined : parseFloat(e.target.value),
                        })
                      }
                    />
                  ) : line.costPrice != null ? (
                    formatMoney(line.costPrice, currency)
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {editable ? (
                    <Form.Control
                      size="sm"
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.price}
                      onChange={(e) => updateItem(index, { price: parseFloat(e.target.value) || 0 })}
                    />
                  ) : (
                    formatMoney(line.price, currency)
                  )}
                </td>
                <td>
                  {editable ? (
                    <div className="input-group input-group-sm">
                      <span className="input-group-text py-0 px-2">
                        {currencySymbol(currency)}
                      </span>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.discountedPrice}
                        onChange={(e) =>
                          updateItem(index, {
                            discountedPrice:
                              e.target.value === '' ? line.price : parseFloat(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                  ) : (
                    formatMoney(line.discountedPrice ?? line.price, currency)
                  )}
                </td>
                <td>{formatMoney(line.lineTotal, currency)}</td>
                <td className="small">{formatMargin(line, currency)}</td>
                {editable && (
                  <td>
                    <Button
                      variant="link"
                      className="text-danger p-0"
                      onClick={() => onRemove?.(index)}
                      aria-label={t('quoteGrid.removeItem')}
                    >
                      <X size={18} />
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default QuoteGrid;
